import { type RowData, type Table } from "@tanstack/react-table";
import cx from "classnames";

import { EmptyState } from "./EmptyState";
import { RenderedCell } from "./RenderedCell";
import { RowControlsMenu } from "./RowControlsMenu";
import { type TableGroup, getRowGroup } from "./TableGroup";
import { rowShouldUseCursorPointer } from "./rowShouldUseCursorPointer";

export type RowSelectBehavior = "none" | "select" | "multiselect";

export type Props<T extends RowData> = {
	table: Table<T>;
	tableGroup?: TableGroup;
	rowSelectBehavior?: RowSelectBehavior;
	/**
	 * Specifies which events cause a row to be selected, e.g., clicking the entire row, or just a
	 * toggle such as a radio button.
	 */
	rowSelectTrigger?: "onClickRow" | "onClickToggle";
};

export function UngroupedRows<T extends RowData>({
	table,
	tableGroup,
	rowSelectBehavior,
	rowSelectTrigger,
}: Props<T>) {
	const rows = table.getRowModel().rows;

	return (
		<>
			{rows.length === 0 && <EmptyState />}
			{rows.length > 0 &&
				rows.map((row) => {
					return (
						<div
							key={row.id}
							className={cx(
								"group/datagrid-row",
								"flex",
								"flex-row",
								"relative",
								"group",
								{
									"cursor-pointer": rowShouldUseCursorPointer(
										rowSelectBehavior,
										rowSelectTrigger
									),
									"!bg-brand-translucent": row.getIsSelected(),
								}
							)}
						>
							<div className="absolute h-[40px] w-[48px] -translate-x-full" />
							{/* <ExpandRowButton<T> row={row} table={table} /> */}
							<div
								className={cx(
									"absolute",
									"left-0",
									"top-1/2",
									"text-right",
									"text-tertiary",
									"opacity-0",
									"transition-opacity",

									"group-hover/table:opacity-100",

									// IMPORTANT: Double group-hover here to override the
									// group-hover above; this selector is "more specific", so it
									// "wins" according to CSS spec.
									"group-hover/table:group-hover/datagrid-row:opacity-0"
								)}
								// className="absolute left-0 top-1/2 text-right text-secondary"
								style={{
									// transform: `translate(-${isGrouped ? "40" : "40"}px, -10px)`,
									transform: `translate(-8px, -9px)`,
								}}
							>
								<div className="right-0 -translate-x-full">
									{(row.index || 0) + 1}
								</div>
							</div>
							<div
								className={cx(
									// Invisible/visible resets, e.g., focus when hover a different row.
									"invisible",
									"group-hover/datagrid-row:visible",

									"absolute",
									"left-0",
									"top-1/2",
									"transition-opacity"
								)}
								style={{
									// transform: `translate(-${isGrouped ? "48" : "40"}px, -11px)`,
									transform: `translate(-24px, -21px)`,
								}}
							>
								<RowControlsMenu table={table} row={row} />
							</div>
							{getRowGroup(row, tableGroup).map((cell) => {
								return <RenderedCell key={cell.id} cell={cell} />;
							})}
						</div>
					);
				})}
		</>
	);
}
